/**
 * ONE adapter for the shared ProfileContactSection (ADR-141 §5).
 *
 * Owns the `/api/v2/me/contact*` data + mutations and the toaster; the
 * shared `@tiaki/profile-ui` section stays presentational (same split as
 * AIM's `ProfileContactAdapter`).
 *
 * Gating: ONE has no FeatureCode enum / feature registry client, so the
 * adapter gates on the endpoint itself — the backend answers 403 when the
 * org lacks the MASS_NOTIFICATION_CONTACTS flag / `mass_notification`
 * Module (or the caller has no org context), `getMyContact` maps that to
 * `null`, and the section renders nothing. Until the first fetch resolves
 * nothing renders either, so feature-off users never see a flash.
 */
import { Box, Text } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  ProfileContactSection,
  type ProfileContactDestination,
} from '@tiaki/profile-ui';
import { useUser } from '@aegix/auth-identity';
import {
  addMyContactDestination,
  getMyContact,
  removeMyContactDestination,
  sendDestinationVerification,
  updateMyContactDestination,
} from '../services/contacts';
import type { MyContactDestination } from '../services/contacts';
import type { OneProfileUser } from './profile/types';
import { toaster } from './ui/toaster';

const QUERY_KEY = ['me-contact'];

/** Normalize the generated (optional-heavy) schema type to the section's shape. */
function toSectionDestination(
  dest: MyContactDestination,
): ProfileContactDestination {
  return {
    id: dest.id,
    channel_code: dest.channel_code,
    address: dest.address ?? '',
    label: dest.label,
    priority: dest.priority ?? 0,
    enabled: dest.enabled ?? true,
    verified_at: dest.verified_at,
    opt_out_at: dest.opt_out_at,
    is_sendable: dest.is_sendable,
  };
}

export function ProfileContactAdapter() {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  // The personal-details phone surfaces as an implicit destination
  // row. ONE's session-probe user IS its fetched user record (auth-identity
  // re-fetches it at boot; ONE has no separate user-detail endpoint), so it
  // is read here directly — mirroring AIM's adapter-internal read.
  const user = useUser<OneProfileUser>();
  const personalPhone = user?.cell_phone ?? null;

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getMyContact,
  });

  // undefined = still loading; null = 403 (capability not available).
  if (data === undefined || data === null) return null;

  const destinations = (data.destinations ?? []).map(toSectionDestination);

  const run = async (fn: () => Promise<unknown>, successTitle: string) => {
    setIsSaving(true);
    try {
      await fn();
      if (successTitle) {
        toaster.create({
          type: 'success',
          duration: 3000,
          title: successTitle,
        });
      }
    } catch (error: unknown) {
      toaster.create({
        type: 'error',
        duration: 5000,
        title: 'Could not update destinations',
        description:
          error instanceof Error && error.message
            ? error.message
            : 'Something went wrong.',
      });
    } finally {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setIsSaving(false);
    }
  };

  return (
    <Box data-e2e="oneProfileContactSection">
      <Text fontSize="sm" color="text.secondary" mb={2}>
        Manage where you receive emergency notifications.
      </Text>
      <ProfileContactSection
        destinations={destinations}
        personalPhone={personalPhone}
        isSaving={isSaving}
        onAdd={(channelCode, address) =>
          run(async () => {
            const added = await addMyContactDestination(channelCode, address);
            // Kick off the §6 verification immediately for email adds
            // (parity with AIM's adapter). Other kinds are not auto-sent —
            // this surface supplies no `renderDestinationVerification`, so a
            // phone code would have nowhere to be entered and the row falls
            // back to admin attestation.
            if (channelCode === 'mail' && !added.verified_at) {
              await sendDestinationVerification(added.id);
            }
          }, 'Destination added — check for a verification message')
        }
        onToggle={(id, nextEnabled) =>
          run(
            () => updateMyContactDestination(id, { enabled: nextEnabled }),
            '',
          )
        }
        onRemove={(id) =>
          run(() => removeMyContactDestination(id), 'Destination removed')
        }
        onResendVerification={(id) =>
          run(() => sendDestinationVerification(id), 'Verification sent')
        }
        e2eTitle="profileContactSection"
      />
    </Box>
  );
}
